import { useEffect, useState } from "react"
import { BsArrowLeft, BsPlusCircleFill } from "react-icons/bs"
import { AiOutlineSearch, AiOutlineMail } from "react-icons/ai"
import { LiaExchangeAltSolid } from "react-icons/lia"
import { IoAddCircleSharp, IoChevronDownSharp } from "react-icons/io5"
import { IoMdCloseCircle, IoMdSettings } from "react-icons/io"
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import AddNewItem from "../components/AddNewItem"
import AddNewCustomer from "../components/AddNewCustomer"
import axios from "axios"
import api from "../config/backend"
import { useNavigate } from "react-router-dom"
import {
    Container,
    Grid,
    TextField,
} from '@mui/material'
import "./css/CreateRecurringInvoice.css"

const repeatIntervals = [
    { name: "Week", value: 7 },
    { name: "2 Weeks", value: 14 },
    { name: "Month", value: 31 },
    { name: "2 Months", value: 62 },
    { name: "3 Months", value: 93 },
    { name: "6 Months", value: 186 },
    { name: "Year", value: 365 },
    { name: "2 Years", value: 730 },
    { name: "3 Years", value: 1095 },
    { name: "Custom", value: "custom" }
]

const customRepeatIntervals = [
    { name: "Day (s)", value: 1 },
    { name: "Week (s)", value: 7 },
    { name: "Month (s)", value: 31 },
    { name: "Year (s)", value: 365 }
]

const CreateRecurringInvoice = ({ setCreateInvoice, setInvoiceUpdatedRefresh }) => {
    const [customerInput, setCustomerInput] = useState("")
    const [customerInputOpen, setCustomerInputOpen] = useState(false)
    const [referenceInput, setReferenceInput] = useState("")
    const [invoiceDate, setInvoiceDate] = useState(undefined)
    const [invoiceExpiryDate, setInvoiceExpiryDate] = useState(undefined)
    const [subjectInput, setSubjectInput] = useState("")
    const [invoiceItems, setInvoiceItems] = useState([
        {
            item: "",
            quantity: "1.00",
            rate: "0.00",
            data: undefined
        }
    ])
    const [discount, setDiscount] = useState("0")
    const [calculatedDiscount, setCalculatedDiscount] = useState("0")

    // Discount is percent or amount
    const [discountUnit, setDiscountUnit] = useState("percentage")
    const [adjustmentsText, setAdjustmentsText] = useState("Adjustments")
    const [adjustmentValue, setAdjustmentValue] = useState("0")
    const [subTotal, setSubTotal] = useState("0")
    const [totalAmount, setTotalAmount] = useState("0")
    const [customerNotes, setCustomerNotes] = useState("Thanks for your business.")
    const [addItemsPopupIndex, setAddItemsPopupIndex] = useState(false)
    const [addNewItemPopupOpen, setAddNewItemPopupOpen] = useState(false)
    const [addNewCustomerPopup, setAddNewCustomerPopup] = useState(false)
    const [productList, setProductList] = useState([])
    const [filteredProductList, setFilteredProductList] = useState([])
    const [productUpdatedRefresh, setProductUpdatedRefresh] = useState(0)
    const [customersList, setCustomersList] = useState([])
    const [filteredCustomersList, setFilteredCustomersList] = useState([])
    const [selectedCustomer, setSelectedCustomer] = useState(undefined)
    const [customersUpdatedRefresh, setCustomersUpdatedRefresh] = useState(0)
    const [error, setError] = useState(false)
    const [repeatInputOpen, setRepeatInputOpen] = useState(false)
    const [customRepeatInputOpen, setCustomRepeatInputOpen] = useState(false)
    const [selectedRepeatIndex, setSelectedRepeatIndex] = useState(0)
    const [customRepeatInterval, setCustomRepeatInterval] = useState(1)
    const [selectedCustomRepeatIndex, setSelectedCustomRepeatIndex] = useState(0)
    const [neverExpires, setNeverExpires] = useState(true)
    const [repeatInterval, setRepeatInterval] = useState(7)

    const token = localStorage.getItem("_crm__token")

    const navigate = useNavigate()

    const fetchProducts = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/fetch-products`, { token })

        if (data.success) {
            setProductList(data.products)
        }
    }

    const fetchCustomers = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/fetch-customers`, { token })

        if (data.success) {
            setCustomersList(data.customers)
        }
    }

    const handleCreateInvoice = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/Recurring/create-invoice`, {
            token,
            customerhash: selectedCustomer && selectedCustomer.hash,
            interval: {
                duration: repeatIntervals[selectedRepeatIndex].name,
                custom: repeatIntervals[selectedRepeatIndex].name === "Custom" ? customRepeatIntervals[selectedCustomRepeatIndex].name : undefined,
                customduration: repeatIntervals[selectedRepeatIndex].name === "Custom" ? customRepeatInterval : undefined
            },
            startdate: invoiceDate,
            expirydate: invoiceExpiryDate,
            subject: subjectInput,
            items: invoiceItems,
            discount: {
                ispercentage: discountUnit === "percentage" ? true : false,
                value: discount
            },
            adjustment: {
                text: adjustmentsText,
                value: adjustmentValue
            },
            note: customerNotes
        })

        if (data.success) {
            setCreateInvoice(false)
            setInvoiceUpdatedRefresh(prev => prev + 1)
        }
        else {
            setError(data.message)
        }
    }

    useEffect(() => {
        fetchProducts()
    }, [productUpdatedRefresh])

    useEffect(() => {
        fetchCustomers()
    }, [customersUpdatedRefresh])

    // Calculate sub total amount
    useEffect(() => {
        let total = 0

        invoiceItems.forEach(item => {
            total += item.quantity * item.rate
        })

        setSubTotal(total)
    }, [invoiceItems])

    // Calculate discount
    useEffect(() => {
        if (discountUnit === "percentage") {
            setCalculatedDiscount((subTotal * discount) / 100)
        }
        else {
            setCalculatedDiscount(discount)
        }
    }, [subTotal, discount, discountUnit])

    // Calculate total amount
    useEffect(() => {
        setTotalAmount(Number(subTotal) + Number(adjustmentValue) - Number(calculatedDiscount))
    }, [subTotal, adjustmentValue, calculatedDiscount])

    // Filter product list based on input
    useEffect(() => {
        if (addItemsPopupIndex === false) {
            return
        }

        setFilteredProductList(productList.filter(product => {
            const input = invoiceItems[addItemsPopupIndex].item

            return product.productname.toLowerCase().includes(input.toLowerCase())
        }))
    }, [productList, invoiceItems, addItemsPopupIndex])

    // Filter customers list based on search input
    useEffect(() => {
        if (customerInput) {
            setFilteredCustomersList(customersList.filter(customer => {
                const input = customerInput.toLowerCase()

                let fullname = customer.firstname.toLowerCase()

                if (customer.lastname) {
                    fullname = fullname + " " + customer.lastname.toLowerCase()
                }

                if (fullname.includes(input)) {
                    return true
                }

                if (customer.email.toLowerCase().includes(input)) {
                    return true
                }

                return false
            }))
        }
        else {
            setFilteredCustomersList(customersList)
        }
    }, [customerInput, customersList])

    // // Calculate repeat intervals
    // useEffect(() => {
    //     const interval = repeatIntervals[selectedRepeatIndex]

    //     if (interval.value === "custom") {
    //         setRepeatInterval(Math.round(customRepeatInterval) * customRepeatIntervals[selectedCustomRepeatIndex].value)
            
    //         return
    //     }
        
    //     setRepeatInterval(interval.value)
    // }, [selectedRepeatIndex, customRepeatInterval, selectedCustomRepeatIndex])

    useEffect(() => {
        let timeout

        if (error) {
            timeout = setTimeout(() => {
                setError(undefined)
            }, 4000)
        }

        return () => clearTimeout(timeout)
    }, [error])

    return (
        (
            <div className="_7zhg">
                {addNewCustomerPopup ? <AddNewCustomer setOpen={setAddNewCustomerPopup} setCustomersUpdatedRefresh={setCustomersUpdatedRefresh} /> : undefined}
                {addNewItemPopupOpen ? <AddNewItem open={addNewItemPopupOpen} setOpen={setAddNewItemPopupOpen} setProductUpdatedRefresh={setProductUpdatedRefresh} /> : undefined}
               <Container>
               <div className="header">
                    <BsArrowLeft onClick={() => setCreateInvoice(false)} />
                    <span>New Recurring Invoice</span>
                    {error ? (
                        <div className="dangle-fog">
                            {error}
                        </div>
                    ) : undefined}
                </div>
                <div className="soothe-far" onClick={() => {
                    customerInputOpen && setCustomerInputOpen(false)
                    addItemsPopupIndex !== false && setAddItemsPopupIndex(false)
                }}>
                    <div className="row">
                        <div className="whingy-peep">
                            <div className="poortith-loch">
                                <div onClick={() => setCustomerInputOpen(true)} className="salients-rap">
                                    {selectedCustomer ? (
                                        selectedCustomer.firstname + " " + selectedCustomer.lastname
                                    ) : "Select or add a customer"}
                                </div>
                                <button onClick={() => setCustomerInputOpen(true)}>
                                    <AiOutlineSearch />
                                </button>
                                {customerInputOpen ? <div onClick={e => e.stopPropagation()} className="cirrus-tee">
                                    <input
                                        value={customerInput}
                                        onChange={e => setCustomerInput(e.target.value)}
                                        placeholder="Search"
                                    />
                                    <div className="pullmans-tom">
                                        {filteredCustomersList.length ? (
                                            filteredCustomersList.map((customer, customerIndex) => {
                                                return (
                                                    <div key={customerIndex} className="poetise-row" onClick={() => {
                                                        setSelectedCustomer(customer)
                                                        setCustomerInputOpen(false)
                                                    }}>
                                                        <div className="winked-flit">
                                                            {customer.firstname} {customer.lastname}
                                                        </div>
                                                        <div className="wights-spar">
                                                            <AiOutlineMail /> {customer.email}
                                                        </div>
                                                        <div className="grifting-dew">
                                                            {customer.currency.code}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <div className="toffies-kook">
                                                No customers
                                            </div>
                                        )}
                                    </div>
                                    <div className="knife-labs" onClick={() => setAddNewCustomerPopup(true)}>
                                        <IoAddCircleSharp />
                                        <span>New Customer</span>
                                    </div>
                                </div> : undefined}
                            </div>
                            {selectedCustomer ? (
                                <div className="bedirty-rub">
                                    {selectedCustomer.currency.icon} {selectedCustomer.currency.code}
                                </div>
                            ) : undefined}
                        </div>
                    </div>
                    {/* <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <div className='primary-first-name'>
                                <TextField value={referenceInput} onChange={e => setReferenceInput(e.target.value)} id="outlined-basic" label="Reference" variant="outlined" />
                            </div>
                        </Grid>
                    </Grid> */}
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <div className='primary-first-name'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start on"
                                        value={invoiceDate}
                                        onChange={(newValue) => setInvoiceDate(newValue)}
                                        minDate={dayjs(new Date())}
                                    />
                                </LocalizationProvider>
                            </div>
                        </Grid>
                        {neverExpires ? undefined : <Grid item xs={4}>
                            <div className='primary-first-name'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Ends on"
                                        value={invoiceExpiryDate}
                                        onChange={(newValue) => setInvoiceExpiryDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                        </Grid>}
                        <Grid item xs={4}>
                            <div className="cumbered-rim">
                                <input
                                    id="never-expires"
                                    type="checkbox"
                                    checked={neverExpires}
                                    onChange={() => setNeverExpires(prev => !prev)}
                                />
                                <label htmlFor="never-expires">Never Expires</label>
                            </div>
                        </Grid>
                    </Grid>
                    <div className="rebake-fib">
                        <div>
                            Repeat Every*
                        </div>
                        <div className="blame-bug">
                            <div className="spiels-east" onClick={() => setRepeatInputOpen(prev => !prev)}>
                                <span>{repeatIntervals[selectedRepeatIndex].name}</span>
                                <IoChevronDownSharp />
                            </div>
                            {repeatInputOpen ? <div className="alecup-tans">
                                <ul>
                                    {repeatIntervals.map((item, index) => {
                                        return (
                                            <li onClick={() => {
                                                setSelectedRepeatIndex(index)
                                                setRepeatInputOpen(false)
                                            }}>{item.name}</li>
                                        )
                                    })}
                                </ul>
                            </div> : undefined}
                        </div>
                        {repeatIntervals[selectedRepeatIndex].value === "custom" ? <div className="crocus-few">
                            <input
                                value={customRepeatInterval}
                                onChange={e => isNaN(e.target.value) === false && setCustomRepeatInterval(e.target.value)}
                            />
                            <div className="blame-bug">
                                <div className="spiels-east" onClick={() => setCustomRepeatInputOpen(prev => !prev)}>
                                    <span>{customRepeatIntervals[selectedCustomRepeatIndex].name}</span>
                                    <IoChevronDownSharp />
                                </div>
                                {customRepeatInputOpen ? <div className="alecup-tans">
                                    <ul>
                                        {customRepeatIntervals.map((item, index) => {
                                            return (
                                                <li onClick={() => {
                                                    setSelectedCustomRepeatIndex(index)
                                                    setCustomRepeatInputOpen(false)
                                                }}>{item.name}</li>
                                            )
                                        })}
                                    </ul>
                                </div> : undefined}
                            </div>
                        </div> : undefined}
                    </div>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <div className='primary-first-name'>
                                <TextField value={subjectInput}
                                    onChange={e => setSubjectInput(e.target.value)} placeholder="Let the customer know what the quote is for" id="outlined-basic" label="Subject" variant="outlined" />
                            </div>
                        </Grid>
                    </Grid>
                    <div className="row">
                        <div className="murphies-nut">
                            <div className="table-header">
                                <div className="item-details">
                                    ITEM DETAILS
                                </div>
                                <div>
                                    QUANTITY
                                </div>
                                <div>
                                    RATE
                                </div>
                                <div>
                                    AMOUNT
                                </div>
                            </div>
                            <div className="table-content">
                                {invoiceItems.map((item, index) => {
                                    return (
                                        <div key={index} className="table-row">
                                            <div className="item-details" onClick={e => {
                                                e.stopPropagation()
                                                setAddItemsPopupIndex(index)
                                            }}>
                                                <textarea
                                                    className="serif-rags"
                                                    value={item.item}
                                                    onChange={e => setInvoiceItems(prev => {
                                                        const copy = JSON.parse(JSON.stringify(prev))
                                                        copy[index].item = e.target.value

                                                        // Remove the prev data - adds up because of selecting a product
                                                        copy[index].data = undefined
                                                        return copy
                                                    })}
                                                    placeholder="Type or click to select an item."
                                                />
                                                {addItemsPopupIndex === index ? (
                                                    <div className="foreguts-pen" onClick={e => e.stopPropagation()}>
                                                        {filteredProductList.length ? filteredProductList.map((product, productIndex) => {
                                                            return (
                                                                <div key={productIndex} className="anarch-oat" onClick={() => {
                                                                    setInvoiceItems(prev => {
                                                                        const copy = JSON.parse(JSON.stringify(prev))
                                                                        copy[index].data = product
                                                                        copy[index].item = product.productname
                                                                        copy[index].rate = product.sellingprice
                                                                        return copy
                                                                    })
                                                                    setAddItemsPopupIndex(false)
                                                                }}>
                                                                    <span>{product.productname}</span>
                                                                    <span>Rate: {product.sellingprice}</span>
                                                                </div>
                                                            )
                                                        }) : (
                                                            <div>
                                                                No products
                                                            </div>
                                                        )}
                                                        <div className="freeway-goal" onClick={() => setAddNewItemPopupOpen(true)}>
                                                            <IoAddCircleSharp />
                                                            <span>Add new item</span>
                                                        </div>
                                                    </div>
                                                ) : undefined}
                                            </div>
                                            <div>
                                                <input
                                                    className="liftoff-just"
                                                    value={item.quantity}
                                                    onChange={e => setInvoiceItems(prev => {
                                                        const copy = JSON.parse(JSON.stringify(prev))
                                                        copy[index].quantity = e.target.value

                                                        return copy
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <input
                                                    className="liftoff-just"
                                                    value={item.rate}
                                                    onChange={e => setInvoiceItems(prev => {
                                                        const copy = JSON.parse(JSON.stringify(prev))
                                                        copy[index].rate = e.target.value

                                                        return copy
                                                    })}
                                                />
                                            </div>
                                            <div className="upholder-eyed">
                                                {(Math.round((item.rate * item.quantity) * 100) / 100).toFixed(2)}
                                            </div>
                                            <div className="hominids-cads" onClick={() => setInvoiceItems(prev => {
                                                const copy = JSON.parse(JSON.stringify(prev))
                                                copy.splice(index, 1)

                                                return copy
                                            })}>
                                                <IoMdCloseCircle />
                                            </div>
                                        </div>
                                    )
                                })
                                }
                            </div>
                        </div>
                    </div>
                    <div className="files-skis row">
                        <div className="agitator-ask">
                            <div>
                                <button className="welsh-cubs" onClick={() => setInvoiceItems(prev => {
                                    const copy = JSON.parse(JSON.stringify(prev))
                                    copy.push({
                                        item: "",
                                        quantity: "1.00",
                                        rate: "0.00",
                                        data: undefined
                                    })
                                    return copy
                                })}>
                                    <BsPlusCircleFill />
                                    <span>Add another line</span>
                                </button>
                            </div>
                            <div>
                                <div>Customer Notes</div>
                                <textarea
                                    value={customerNotes}
                                    onChange={e => setCustomerNotes(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="adepts-hope">
                            <div className="imitator-doty">
                                <div className="atheise-heir networks-oak">
                                    <div>
                                        Sub Total
                                    </div>
                                    <div className="icing-bit">

                                    </div>
                                    <div>
                                        {(Math.round(subTotal * 100) / 100).toFixed(2)}
                                    </div>
                                </div>
                                <div className="networks-oak">
                                    <div>
                                        Discount
                                    </div>
                                    <div className="jibes-het icing-bit">
                                        <input
                                            value={discount}
                                            onChange={e => setDiscount(e.target.value)}
                                        />
                                        <div className="shallops-onus">
                                            <span>{discountUnit === "percentage" ? "%" : "₹"}</span>
                                            <div className="godetias-doom" onClick={() => setDiscountUnit(prev => {
                                                return prev === "percentage" ? "amount" : "percentage"
                                            })}>
                                                <LiaExchangeAltSolid />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {(Math.round(calculatedDiscount * 100) / 100) > 0 ? "-" : undefined}{(Math.round(calculatedDiscount * 100) / 100).toFixed(2)}
                                    </div>
                                </div>
                                <div className="networks-oak">
                                    <div>
                                        <input
                                            className="coxalgic-say"
                                            value={adjustmentsText}
                                            onChange={e => setAdjustmentsText(e.target.value)}
                                        />
                                    </div>
                                    <div className="icing-bit">
                                        <input
                                            value={adjustmentValue}
                                            onChange={e => setAdjustmentValue(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        {(Math.round(adjustmentValue * 100) / 100).toFixed(2)}
                                    </div>
                                </div>
                                <div className="mulling-guy networks-oak">
                                    <div>
                                        Total
                                    </div>
                                    <div className="icing-bit">

                                    </div>
                                    <div>
                                        ₹ {(Math.round(totalAmount * 100) / 100).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="footrot-pip">
                        <button className="ballroom-tab" onClick={() => handleCreateInvoice()}>
                            Save and Send
                        </button>
                        <button className="satin-net">
                            Cancel
                        </button>
                    </div>
                </div>
               </Container>
            </div>
        )
    )
}

export default CreateRecurringInvoice
